import React, { useState } from 'react';
import StrongsModal from './StrongsModal';

/**
 * Renders verse text that may contain Strong's tags `<w s='H7225'>word</w>`.
 * Tapping any tagged word opens the unified Word Study popover (StrongsModal),
 * which shows the definition + usage stats + full concordance. This is the
 * single shared word-study surface used everywhere tagged verses appear.
 */
const STRONGS_RE = /<w\s+s=['"]([HG]\d+)['"]>([^<]+)<\/w>/g;

const parseStrongsText = (text) => {
  if (!text) return [];
  const parts = [];
  let lastIndex = 0;
  let match;
  STRONGS_RE.lastIndex = 0;
  while ((match = STRONGS_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
    }
    parts.push({ type: 'strongs', number: match[1], word: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }
  return parts;
};

const StrongsVerseText = ({
  text,
  fontSize = 'base',
  fontFamily = 'serif',
  className = '',
  translation = 'KJV',
}) => {
  const [activeStrongs, setActiveStrongs] = useState(null);

  const fontSizeClasses = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
  };
  const fontFamilyClasses = { serif: 'font-serif', sans: 'font-sans', mono: 'font-mono' };

  const parts = parseStrongsText(text);

  return (
    <>
      <p
        className={`leading-relaxed text-foreground ${fontSizeClasses[fontSize]} ${fontFamilyClasses[fontFamily]} ${className}`}
        data-testid="strongs-verse-text"
      >
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return <span key={index}>{part.content}</span>;
          }
          return (
            <span
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setActiveStrongs(part.number);
              }}
              className="relative inline-block cursor-pointer text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border-b border-blue-400/60 dark:border-blue-600/60 hover:border-blue-600 dark:hover:border-blue-400 transition-colors"
              title={`Word study · Strong's ${part.number}`}
              data-testid={`strongs-word-${part.number}`}
            >
              {part.word}
              <sup className="text-[10px] ml-0.5 font-mono text-blue-500">{part.number}</sup>
            </span>
          );
        })}
      </p>

      <StrongsModal
        strongsId={activeStrongs}
        isOpen={!!activeStrongs}
        onClose={() => setActiveStrongs(null)}
        translation={translation}
      />
    </>
  );
};

export default StrongsVerseText;
